import { InterviewKind } from '@Domain/Enums/Interviews/InterviewKind';
import type InterviewView from '@UI/Store/Interviews/Views/InterviewView';

/**
 * Reference to an interview: its name and date, worded by kind. A note-taking session reads
 * "{name} — prise de notes du {date}", a conversation or an entered text "{name} — {date}" (a text's
 * name already says "Texte saisi"); without a name, "Entretien du {date}" / "Prise de notes du {date}" /
 * "Texte saisi du {date}". Degrades to "… n° {id}" when the date is unknown, or to "Entretien n° {id}"
 * when the interview is not in the loaded list. The wording is written here from `kind.value`, never
 * read from the server label.
 */
const formatInterviewReference = (interview: InterviewView | null, interviewId: number): string => {
	if (interview === null) {
		return `Entretien n° ${interviewId}`;
	}

	const name = interview.name?.trim() ?? '';
	const dateDisplay = interview.conductedOn !== null ? interview.conductedOn.toDisplay() : null;

	if (name !== '') {
		if (interview.kind.value === InterviewKind.NOTES) {
			return dateDisplay !== null
				? `${name} — prise de notes du ${dateDisplay}`
				: `${name} — prise de notes`;
		}

		return dateDisplay !== null ? `${name} — ${dateDisplay}` : name;
	}

	if (interview.kind.value === InterviewKind.NOTES) {
		return dateDisplay !== null ? `Prise de notes du ${dateDisplay}` : `Prise de notes n° ${interviewId}`;
	}

	if (interview.kind.value === InterviewKind.TEXT) {
		return dateDisplay !== null ? `Texte saisi du ${dateDisplay}` : `Texte saisi n° ${interviewId}`;
	}

	return dateDisplay !== null ? `Entretien du ${dateDisplay}` : `Entretien n° ${interviewId}`;
};

export default formatInterviewReference;
